/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
(() => {
  if (window.customElements.get('mobile-panels')) return;

  const panelTransitionMs = 500; /* matches .mobile-panel transition */
  const drawerTransitionMs = 600;

  class MobilePanels extends HTMLElement {
    connectedCallback() {
      /* append-body re-runs this when the drawer moves to <body> */
      if (this.initialized) return;
      this.initialized = true;

      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-panel-target]');
        const target = trigger && this.querySelector(`#${trigger.dataset.panelTarget}`);
        if (!target) return;
        event.preventDefault();
        this.show(target, false);
      });

      const drawer = this.closest('mobile-navigation');
      if (!drawer) return;

      this.closeButton = drawer.querySelector('.drawer__header [data-action="close"]');
      this.backButton = drawer.querySelector('.drawer__header .mobile-panels__back');
      this.headingElement = drawer.querySelector('.drawer__header .mobile-panel__heading');

      this.backButton?.addEventListener('click', () => {
        const parent = this.activePanel && this.querySelector(`#${this.activePanel.dataset.parentPanel}`);
        if (parent) this.show(parent, true);
      });

      drawer.addEventListener('mobile-nav:close', () => setTimeout(() => this.reset(), drawerTransitionMs));
    }

    get activePanel() {
      return this.querySelector('.mobile-panel--active');
    }

    show(target, isBack) {
      const current = this.activePanel;
      if (!current || current === target) return;

      if (isBack) {
        target.classList.remove('mobile-panel--behind');
      } else {
        current.classList.add('mobile-panel--behind');
        target.scrollTop = 0;
      }

      current.classList.remove('mobile-panel--active');
      target.classList.add('mobile-panel--active');
      this.syncHeader(target);

      clearTimeout(this.focusTimeout);
      this.focusTimeout = setTimeout(() => target.focus({preventScroll: true}), panelTransitionMs);
    }

    syncHeader(activePanel) {
      const isSub = !activePanel.hasAttribute('data-main-panel');
      if (this.closeButton) this.closeButton.hidden = isSub;
      if (this.backButton) this.backButton.hidden = !isSub;
      if (!this.headingElement) return;

      this.headingElement.hidden = !isSub;
      this.headingElement.textContent = isSub ? activePanel.dataset.panelTitle || '' : '';
      if (isSub && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.headingElement.animate({opacity: [0, 1]}, {duration: 200, easing: 'ease'});
      }
    }

    reset() {
      const panels = this.querySelectorAll('.mobile-panel');
      const main = this.querySelector('[data-main-panel]') || panels[0];

      this.classList.add('mobile-panels--no-transition');
      panels.forEach((panel) => panel.classList.remove('mobile-panel--active', 'mobile-panel--behind'));
      this.querySelectorAll('.mobile-nav__item--group > input').forEach((input) => input.checked = false);

      if (main) {
        main.classList.add('mobile-panel--active');
        main.scrollTop = 0;
        this.syncHeader(main);
      }

      this.offsetHeight; /* force reflow so the reset doesn't animate */
      this.classList.remove('mobile-panels--no-transition');
    }
  }

  window.customElements.define('mobile-panels', MobilePanels);
})();

document.addEventListener('click', (event) => {
  const button = event.target.closest('.selectvehicle');
  const ymm = document.querySelector('.header-ymm');
  if (!button || !ymm) return;
  const open = ymm.classList.toggle('open');
  button.setAttribute('aria-expanded', open);
  /* the panel sits in its own section below the sticky header; pin it under the header so it opens in view when scrolled */
  if (open) ymm.closest('.shopify-section').style.top = `${button.closest('.shopify-section').getBoundingClientRect().bottom}px`;
});

(() => {
  const initProgress = (wrapper) => {
    const bar = wrapper.querySelector('.progress-bar');
    const scroller = wrapper.parentElement.querySelector(wrapper.dataset.progressFor);
    if (!bar || !scroller || wrapper.dataset.progressReady) return;
    wrapper.dataset.progressReady = 'true';

    scroller.addEventListener('scroll', () => {
      const range = scroller.scrollWidth - scroller.clientWidth;
      bar.style.setProperty('--transform', `${range > 0 ? Math.abs(scroller.scrollLeft) / range * 100 : 0}%`);
    }, {passive: true});
  };

  const initDots = (dots) => {
    const scroller = dots.parentElement.querySelector(dots.dataset.dotsFor);
    const buttons = dots.querySelectorAll('button');
    if (!scroller || dots.dataset.dotsReady) return;
    dots.dataset.dotsReady = 'true';

    buttons.forEach((button, i) => button.addEventListener('click', () => {
      scroller.children[i]?.scrollIntoView({inline: 'center', block: 'nearest', behavior: 'smooth'});
    }));

    scroller.addEventListener('scroll', () => {
      const range = scroller.scrollWidth - scroller.clientWidth;
      const active = range > 0 ? Math.round(Math.abs(scroller.scrollLeft) / range * (buttons.length - 1)) : 0;
      buttons.forEach((button, i) => button.classList.toggle('active', i === active));
    }, {passive: true});
  };

  const initIndicators = (root) => {
    root.querySelectorAll('[data-progress-for]').forEach(initProgress);
    root.querySelectorAll('[data-dots-for]').forEach(initDots);
  };

  initIndicators(document);
  document.addEventListener('shopify:section:load', (event) => initIndicators(event.target));
})();

/**
 * Dropship Fee: companies flagged mgp.dropship get exactly one $8.50 Dropship Fee line
 * while the cart holds anything else. The mgp-checkout-rules validation blocks checkout if not.
 */
(() => {
  const feeId = window.themeVariables?.settings?.dropshipFeeVariantId;
  if (!feeId) return;

  let busy = false;

  const syncFee = async () => {
    if (busy) return;
    busy = true;
    try {
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      const feeLines = cart.items.filter((item) => item.variant_id === feeId);
      const feeQty = feeLines.reduce((sum, item) => sum + item.quantity, 0);
      const hasProducts = cart.items.some((item) => item.variant_id !== feeId);

      let response = null;
      if (hasProducts && feeQty === 0) {
        response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({items: [{id: feeId, quantity: 1}]})
        });
      } else if (feeLines.length && (!hasProducts || feeQty > 1)) {
        const updates = {};
        feeLines.forEach((item, i) => updates[item.key] = hasProducts && i === 0 ? 1 : 0);
        response = await fetch(`${Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({updates})
        });
      }

      if (response?.ok) {
        if (window.themeVariables.settings.pageType === 'cart') {
          window.location.reload();
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true}));
        }
      }
    } finally {
      busy = false;
    }
  };

  syncFee();
  document.documentElement.addEventListener('cart:updated', syncFee);
  document.documentElement.addEventListener('cart:refresh', syncFee);
})();

/***** VEHICLE BAR OPEN ON LOAD (catch-all collection) *****/
(() => {
  if (!document.querySelector('[data-ymm-open]')) return;
  const ymm = document.querySelector('.header-ymm');
  if (!ymm) return;
  ymm.classList.add('open', 'header-ymm--inline');
  document.querySelector('.selectvehicle')?.setAttribute('aria-expanded', 'true');
})();

/***** COLLECTION DESCRIPTION READ MORE *****/
document.querySelectorAll('.mgp-desc').forEach((desc) => {
  const button = desc.nextElementSibling;
  if (!button?.classList.contains('mgp-descmore') || desc.scrollHeight <= desc.clientHeight + 1) return;
  button.hidden = false;
  button.addEventListener('click', () => {
    const open = desc.classList.toggle('is-open');
    button.setAttribute('aria-expanded', open);
    button.textContent = open ? 'Read less' : 'Read more';
  });
});

/***** COLLECTIONS INDEX *****/
document.addEventListener('click', (event) => {
  const button = event.target.closest('.cidx_vehiclebtn');
  const chip = document.querySelector('.selectvehicle');
  if (!button || !chip) return;
  chip.click();
  button.setAttribute('aria-expanded', chip.getAttribute('aria-expanded'));
});

/***** CART DRAWER FOCUS RETURN *****/
(() => {
  let cartSubmitter = null;
  document.addEventListener('submit', (event) => cartSubmitter = event.submitter, true);
  document.addEventListener('variant:added', () => {
    if (!cartSubmitter || document.activeElement !== document.body) return;
    cartSubmitter.focus({preventScroll: true});
  });
})();

/***** BLOG ELEMENTOR GALLERIES *****/
document.querySelectorAll('.rte .e-gallery-image[data-thumbnail]').forEach((tile) => {
  const src = tile.dataset.thumbnail;
  if (!src.startsWith('https://cdn.shopify.com/') || tile.querySelector('img')) return;
  const img = document.createElement('img');
  img.src = src;
  img.alt = tile.getAttribute('aria-label') || '';
  img.loading = 'lazy';
  img.decoding = 'async';
  tile.removeAttribute('role');
  tile.append(img);
});

// Convermax filters: hide the Custom Graphics (CUSDF) facet values. Those variants sell through the customizer only (Boogie 10-30).
(() => {
  const hidden = ['Designated Finish', 'Custom Graphics (Engraved)'];
  const run = () => document.querySelectorAll('.facetdiv').forEach((d) => {
    const k = d.querySelector('.facetentrykey');
    if (k && hidden.includes(k.textContent.trim())) d.style.display = 'none';
  });
  let t;
  new MutationObserver(() => { clearTimeout(t); t = setTimeout(run, 50); })
    .observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', run);
})();

/***** DIGITALLY PRINTED COLLECTION: CARDS OPEN THE DIGITALLY PRINTED DESIGN *****/
(() => {
  if (!location.pathname.endsWith('/collections/digitally-printed-caliper-covers')) return;
  const cache = {};
  const pick = (handle) => cache[handle] || (cache[handle] = fetch(`/products/${handle}.js`)
    .then((r) => (r.ok ? r.json() : null))
    .then((p) => {
      const dp = (p?.variants || []).filter((v) => v.title.includes('(Digitally Printed)'));
      const img = dp.filter((v) => v.featured_image);
      return img.find((v) => v.option1 === 'Red') || img[0] || dp[0] || null;
    })
    .catch(() => null));

  const run = () => {
    document.querySelectorAll('.cm_product-item').forEach((item) => {
      const link = item.querySelector('.cm_product-item__title');
      const handle = (link?.getAttribute('href') || '').split('/products/')[1]?.split(/[?#]/)[0];
      if (!handle || item.dataset.mgpDp === handle) return;
      item.dataset.mgpDp = handle;
      pick(handle).then((v) => {
        if (!v) return;
        const url = `/products/${handle}?variant=${v.id}`;
        item.querySelectorAll('a.cm_product-item__image-container, a.cm_product-item__title').forEach((a) => a.href = url);
        const sku = item.querySelector('.cm_product-item__sku .sku-value');
        if (sku && v.sku) sku.textContent = v.sku;
        if (!v.featured_image) return;
        const src = v.featured_image.src.replace(/^https?:/, '');
        item.querySelectorAll('.cm_product-item__image').forEach((img) => {
          img.src = `${src}${src.includes('?') ? '&' : '?'}width=600`;
          img.removeAttribute('srcset');
          img.alt = v.featured_image.alt || v.name;
        });
      });
    });
  };

  let t;
  new MutationObserver(() => { clearTimeout(t); t = setTimeout(run, 150); })
    .observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', run);
})();

/***** HEADER VEHICLE BUTTON SHOWS THE SELECTED VEHICLE *****/
(() => {
  const label = document.querySelector('.selectvehicle .vehiclelabel');
  if (!label) return;
  const run = () => {
    const cm = window.Convermax;
    const text = cm?.isVehicleSelected?.() ? 'VEHICLE' : 'SELECT A VEHICLE';
    if (label.textContent !== text) label.textContent = text;
  };
  let t;
  new MutationObserver(() => { clearTimeout(t); t = setTimeout(run, 150); })
    .observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', run);
})();
